import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { LayoutDashboard, Disc3, Bot, Users, Settings, LogOut, Moon, Sun, Radio, Mic2, Link2, CalendarDays, Menu, X, Languages, BarChart3, Wallet, DownloadCloud } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { api } from "@/lib/api";
import { ROLE_LABEL } from "@/constants/catalog";
import { t, useLang } from "@/i18n";
import { cn } from "@/lib/utils";

const NAV = [
  { group: "Catálogo", items: [
    { to: "/", label: "Panel", icon: LayoutDashboard, id: "nav-link-panel", roles: ["admin", "manager", "artist"] },
    { to: "/lanzamientos", label: "Lanzamientos", icon: Disc3, id: "nav-link-lanzamientos", roles: ["admin", "manager", "artist"] },
    { to: "/artistas", label: "Artistas", icon: Mic2, id: "nav-link-artistas", roles: ["admin", "manager", "artist"] },
    { to: "/calendario", label: "Calendario", icon: CalendarDays, id: "nav-link-calendario", roles: ["admin", "manager", "artist"] },
    { to: "/importar", label: "Importar", icon: DownloadCloud, id: "nav-link-importar", roles: ["admin", "manager", "artist"] },
  ] },
  { group: "Promoción", items: [
    { to: "/smartlinks", label: "Smartlinks", icon: Link2, id: "nav-link-smartlinks", roles: ["admin", "manager", "artist"] },
    { to: "/analiticas", label: "Analíticas", icon: BarChart3, id: "nav-link-analiticas", roles: ["admin", "manager", "artist"] },
  ] },
  { group: "Finanzas", items: [
    { to: "/pagos", label: "Pagos", icon: Wallet, id: "nav-link-pagos", roles: ["admin", "manager", "artist"] },
  ] },
  { group: "Operación", items: [
    { to: "/bot", label: "Bot Downtown", icon: Bot, id: "nav-link-downtown-bot", roles: ["admin", "manager"] },
    { to: "/equipo", label: "Equipo", icon: Users, id: "nav-link-equipo", roles: ["admin", "manager"] },
    { to: "/ajustes", label: "Ajustes", icon: Settings, id: "nav-link-ajustes", roles: ["admin", "manager"] },
  ] },
];

export const useTheme = () => {
  const [dark, setDark] = useState(() => localStorage.getItem("dobler_theme") !== "light");
  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
    localStorage.setItem("dobler_theme", dark ? "dark" : "light");
  }, [dark]);
  return [dark, setDark];
};

const DowntownPill = () => {
  const [s, setS] = useState(null);
  const { can } = useAuth();
  useEffect(() => { if (can("admin", "manager")) api.get("/settings/downtown").then((r) => setS(r.data)).catch(() => {}); }, [can]);
  if (!s) return null;
  const connected = s.has_saved_session || s.has_session_cookie || s.has_password;
  return (
    <span data-testid="downtown-status-pill" className={cn("hidden md:inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium",
      connected ? "border-primary/40 text-primary bg-primary/10" : "border-amber-400/40 text-amber-500 bg-amber-500/10")}>
      <Radio className="h-3 w-3" /> Downtown: {connected ? (s.has_saved_session ? "sesión guardada" : "credenciales listas") : "modo simulación"}
    </span>
  );
};

const LangToggle = () => {
  const [lang, setLang] = useLang();
  return (
    <button data-testid="lang-toggle" onClick={() => setLang(lang === "es" ? "en" : "es")} title={t("Cambiar idioma")}
      className="h-8 px-2.5 inline-flex items-center gap-1.5 rounded-md border border-border text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors">
      <Languages className="h-3.5 w-3.5" /> {lang === "es" ? "ES" : "EN"}
    </button>
  );
};

const SideNav = ({ role, onNavigate }) => (
  <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-6">
    {NAV.map((g) => {
      const items = g.items.filter((n) => n.roles.includes(role));
      if (!items.length) return null;
      return (
        <div key={g.group}>
          <p className="eyebrow px-3 mb-2">{t(g.group)}</p>
          <ul className="space-y-0.5">
            {items.map((n) => (
              <li key={n.to}>
                <NavLink to={n.to} end={n.to === "/"} data-testid={n.id} onClick={onNavigate}
                  className={({ isActive }) => cn("group relative flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors",
                    isActive ? "text-primary font-medium" : "text-muted-foreground hover:text-foreground hover:bg-muted")}>
                  {({ isActive }) => (<>
                    {isActive && <motion.span layoutId="nav-active" className="absolute inset-0 rounded-md bg-primary/15 ring-1 ring-primary/20" transition={{ type: "spring", stiffness: 380, damping: 32 }} />}
                    {isActive && <motion.span layoutId="nav-active-bar" className="absolute left-0 top-2 bottom-2 w-0.5 rounded-full bg-primary" transition={{ type: "spring", stiffness: 380, damping: 32 }} />}
                    <n.icon className="relative h-4 w-4 shrink-0 transition-transform duration-300 group-hover:scale-110" /><span className="relative">{t(n.label)}</span>
                  </>)}
                </NavLink>
              </li>
            ))}
          </ul>
        </div>
      );
    })}
  </nav>
);

export default function Layout() {
  const { user, logout } = useAuth();
  const [dark, setDark] = useTheme();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const initials = user.name.split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();
  const onMove = (e) => {
    const el = e.target.closest?.(".panel"); if (!el) return;
    const r = el.getBoundingClientRect();
    el.style.setProperty("--mx", `${e.clientX - r.left}px`); el.style.setProperty("--my", `${e.clientY - r.top}px`);
  };

  return (
    <div className="app-bg grain min-h-screen lg:grid lg:grid-cols-[248px_1fr]" onMouseMove={onMove}>
      <aside className={cn("fixed inset-y-0 left-0 z-50 w-[248px] bg-background/95 backdrop-blur-xl border-r border-border flex flex-col transition-transform lg:static lg:translate-x-0",
        open ? "translate-x-0" : "-translate-x-full")} data-testid="sidebar">
        <div className="h-16 px-5 flex items-center justify-between border-b border-border">
          <NavLink to="/" className="flex items-center gap-2.5" data-testid="brand-logo" onClick={() => setOpen(false)}>
            <Logo className="h-9 w-9" />
            <span className="font-display font-bold tracking-tight">Doble R <span className="text-primary">Records</span></span>
          </NavLink>
          <button className="lg:hidden text-muted-foreground" onClick={() => setOpen(false)} data-testid="sidebar-close"><X className="h-5 w-5" /></button>
        </div>
        <SideNav role={user.role} onNavigate={() => setOpen(false)} />
        <div className="border-t border-border p-4 flex items-center gap-3">
          <div className="h-9 w-9 rounded-full bg-primary/15 text-primary grid place-items-center font-display font-bold text-sm">{initials}</div>
          <div className="min-w-0 flex-1 leading-tight">
            <p className="text-sm font-medium truncate" data-testid="user-name">{user.name}</p>
            <p className="text-[11px] text-muted-foreground">{t(ROLE_LABEL[user.role])}</p>
          </div>
          <button data-testid="logout-button" onClick={() => { logout(); navigate("/login"); }} className="h-8 w-8 grid place-items-center rounded-md hover:bg-muted text-muted-foreground transition-colors" title={t("Cerrar sesión")}>
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </aside>
      {open && <div className="fixed inset-0 z-40 bg-black/60 lg:hidden" onClick={() => setOpen(false)} />}
      <div className="min-w-0 flex flex-col min-h-screen">
        <header className="h-16 bg-background/70 backdrop-blur-xl border-b border-border z-30 sticky top-0 px-4 sm:px-6 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <button className="lg:hidden h-9 w-9 grid place-items-center rounded-md hover:bg-muted text-muted-foreground" onClick={() => setOpen(true)} data-testid="sidebar-open"><Menu className="h-5 w-5" /></button>
            <span className="lg:hidden font-display font-bold">Doble R <span className="text-primary">Records</span></span>
          </div>
          <div className="flex items-center gap-2">
            <DowntownPill />
            <LangToggle />
            <button data-testid="theme-toggle" onClick={() => setDark(!dark)} className="h-8 w-8 grid place-items-center rounded-md border border-border hover:bg-muted text-muted-foreground transition-colors">
              {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>
          </div>
        </header>
        <main className="relative z-[2] flex-1 w-full max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-8 lg:py-10">
          <motion.div key={pathname} className="page" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, ease: [0.2, 0.8, 0.2, 1] }}>
            <Outlet />
          </motion.div>
        </main>
      </div>
    </div>
  );
}
